/**
 * Simple debounce function implementation
 *
 * This is a lightweight replacement for lodash.debounce that works in both
 * browser and server-side rendering environments.
 *
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @param {boolean} immediate - Whether to call the function immediately
 * @returns {Function} - The debounced function
 */
export function debounce(func, wait = 300, immediate = false) {
  let timeout

  return function executedFunction(...args) {
    const context = this

    const later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }

    const callNow = immediate && !timeout

    clearTimeout(timeout)

    timeout = setTimeout(later, wait)

    if (callNow) func.apply(context, args)
  }
}

/**
 * Simple throttle function implementation
 *
 * This is a lightweight replacement for lodash.throttle that works in both
 * browser and server-side rendering environments.
 *
 * @param {Function} func - The function to throttle
 * @param {number} wait - The number of milliseconds to delay between executions
 * @returns {Function} - The throttled function
 */
export function throttle(func, wait = 300) {
  let lastCall = 0

  return function (...args) {
    const now = Date.now()

    if (now - lastCall >= wait) {
      func.apply(this, args)
      lastCall = now
    }
  }
}
